import React, { useEffect, useState } from 'react'
import { Avatar, Badge, Box, Divider, IconButton, ListItemIcon, ListItemText, Menu, MenuItem, Tooltip } from '@mui/material';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';
import AssignmentIndOutlinedIcon from '@mui/icons-material/AssignmentIndOutlined';
import WorkOutlineRoundedIcon from '@mui/icons-material/WorkOutlineRounded';
import FavoriteBorderRoundedIcon from '@mui/icons-material/FavoriteBorderRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import AdminPanelSettingsOutlinedIcon from '@mui/icons-material/AdminPanelSettingsOutlined';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getUserTalentByUserId } from '../../services/UserTalentService';
import useAuth from '../../common/hooks/useAuth';
import { brand } from '../../theme';

export default function UserContainer({ handleLogout }) {
    const { t } = useTranslation();
    const { user } = useAuth();
    const [anchorEl, setAnchorEl] = useState(null);
    const [talentUser, setTalentUser] = useState(null);
    const navigate = useNavigate();
    const open = Boolean(anchorEl);

    useEffect(() => {
        if (!user?.userId) return;
        getUserTalentByUserId(user.userId)
            .then((response) => setTalentUser(response.data))
            .catch(() => null);
    }, [user?.userId, open]);

    if (!user) return null;

    const talentIncomplete = talentUser && !talentUser.id;
    const talentPrivate = talentUser && talentUser.id && !talentUser.isPublic;
    const initials = `${(user.name || user.username || 'U').charAt(0)}${(user.surname || '').charAt(0)}`.toUpperCase();

    const go = (path) => {
        setAnchorEl(null);
        navigate(path);
    };

    return (
        <>
            <Tooltip title={t('user.accountSettingsTooltip')}>
                <IconButton
                    onClick={(e) => setAnchorEl(e.currentTarget)}
                    aria-controls={open ? 'account-menu' : undefined}
                    aria-haspopup="true"
                    aria-expanded={open ? 'true' : undefined}
                    aria-label={t('user.accountSettingsTooltip')}
                    sx={{ p: 0.5 }}
                >
                    <Badge color="error" variant="dot" invisible={!talentIncomplete} overlap="circular">
                        <Avatar sx={{ width: 34, height: 34, fontSize: '0.9rem', fontWeight: 800, background: brand.gradient }}>{initials}</Avatar>
                    </Badge>
                </IconButton>
            </Tooltip>
            <Menu
                anchorEl={anchorEl}
                id="account-menu"
                open={open}
                onClose={() => setAnchorEl(null)}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                PaperProps={{ sx: { mt: 1, minWidth: 260, maxWidth: 'calc(100vw - 24px)' } }}
            >
                <Box sx={{ px: 2, py: 1.5, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Avatar sx={{ width: 42, height: 42, fontWeight: 800, background: brand.gradient }}>{initials}</Avatar>
                    <ListItemText
                        primary={[user.name, user.surname].filter(Boolean).join(' ') || user.username}
                        secondary={user.email}
                        primaryTypographyProps={{ fontWeight: 800, noWrap: true }}
                        secondaryTypographyProps={{ noWrap: true }}
                        sx={{ minWidth: 0 }}
                    />
                </Box>
                <Divider />
                <MenuItem onClick={() => go('/your-posts')}>
                    <ListItemIcon><WorkOutlineRoundedIcon fontSize="small" /></ListItemIcon>
                    {t('nav.yourJobs')}
                </MenuItem>
                <MenuItem onClick={() => go('/favourites')}>
                    <ListItemIcon><FavoriteBorderRoundedIcon fontSize="small" /></ListItemIcon>
                    {t('nav.favourites')}
                </MenuItem>
                <MenuItem onClick={() => go('/manage-talent-user')}>
                    <ListItemIcon><AssignmentIndOutlinedIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary={t('user.talentAccount')} secondary={talentIncomplete ? t('user.completeTalentTooltip') : null} secondaryTypographyProps={{ color: 'error.main', fontWeight: 600 }} />
                    {talentPrivate && (
                        <Tooltip title={t('user.publicProfileTooltip')}>
                            <WarningAmberRoundedIcon fontSize="small" sx={{ color: '#F59E0B', ml: 1 }} />
                        </Tooltip>
                    )}
                </MenuItem>
                {user.role === 'ROLE_ADMIN' && (
                    <MenuItem onClick={() => go('/admin')} sx={{ color: 'primary.main', fontWeight: 700 }}>
                        <ListItemIcon><AdminPanelSettingsOutlinedIcon fontSize="small" color="primary" /></ListItemIcon>
                        {t('admin.title')}
                    </MenuItem>
                )}
                <MenuItem onClick={() => go('/manage-user')}>
                    <ListItemIcon><SettingsOutlinedIcon fontSize="small" /></ListItemIcon>
                    {t('user.settingsAccount')}
                </MenuItem>
                <Divider />
                <MenuItem onClick={() => { setAnchorEl(null); handleLogout(); }} sx={{ color: 'error.main' }}>
                    <ListItemIcon><LogoutRoundedIcon fontSize="small" sx={{ color: 'error.main' }} /></ListItemIcon>
                    {t('nav.logout')}
                </MenuItem>
            </Menu>
        </>
    );
}
